import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import * as notificheApi from "../api/notifiche";
import { Notifica } from "../api/notifiche";

/**
 * Campanella notifiche in-app (Fase 3.4) per chi ne riceve: admin, staff e chi
 * ha `ritiri:read` (lo decide `AppLayout`). Mostra il numero di
 * notifiche non lette e, al click, un pannello con le recenti. Cliccando una
 * notifica la segna come letta e (se collegata a un sopralluogo) porta alla
 * pagina Sopralluoghi. Polling leggero ogni 60s per aggiornare il badge.
 */
export function NotificheBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifiche, setNotifiche] = useState<Notifica[]>([]);
  const [nonLette, setNonLette] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  async function load() {
    try {
      const { notifiche, non_lette } = await notificheApi.listNotifiche();
      setNotifiche(notifiche);
      setNonLette(non_lette);
    } catch {
      /* silenzioso: la campanella non deve rompere il layout */
    }
  }

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, []);

  // Chiudi il pannello cliccando fuori.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  async function handleClickNotifica(n: Notifica) {
    if (!n.letta) {
      await notificheApi.segnaLetta(n.id);
      setNonLette((c) => Math.max(0, c - 1));
      setNotifiche((list) => list.map((x) => (x.id === n.id ? { ...x, letta: true } : x)));
    }
    setOpen(false);
    if (n.entity_type === "sopralluogo") navigate("/sopralluoghi");
    // Ritiro non effettuato (Ritiri valori): in cima alla dashboard ci sono avviso e badge.
    if (n.entity_type === "ritiro_richiesta") navigate("/ritiri-valori");
  }

  async function handleSegnaTutte() {
    await notificheApi.segnaTutteLette();
    setNonLette(0);
    setNotifiche((list) => list.map((x) => ({ ...x, letta: true })));
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifiche${nonLette > 0 ? ` (${nonLette} non lette)` : ""}`}
        className="relative rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {nonLette > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-ink">
            {nonLette > 9 ? "9+" : nonLette}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-line bg-white text-ink shadow-lift">
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="text-sm font-semibold">Notifiche</span>
            {nonLette > 0 && (
              <button type="button" onClick={handleSegnaTutte} className="text-xs text-primary hover:underline">
                Segna tutte come lette
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifiche.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-ink/40">Nessuna notifica.</p>
            ) : (
              notifiche.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleClickNotifica(n)}
                  className={`flex w-full flex-col items-start gap-0.5 border-b border-line px-3 py-2 text-left transition hover:bg-ink/[0.03] ${
                    n.letta ? "opacity-60" : ""
                  }`}
                >
                  <span className="flex w-full items-start gap-2 text-sm text-ink">
                    {!n.letta && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent" aria-hidden="true" />}
                    <span className={n.letta ? "" : "font-medium"}>{n.messaggio}</span>
                  </span>
                  <span className="pl-4 text-xs text-ink/40">
                    {new Date(n.created_at).toLocaleString("it-IT")}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
